import * as Sentry from '@sentry/react';

type FetchOptions<Params = unknown> = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: Params;
  params?: Record<string, string | number>;
  timeout?: number; // Timeout in milliseconds
} & Omit<RequestInit, 'method' | 'headers' | 'body'>;

type FetchResponse<T> = T;

/**
 * An HTTP error that kept its status code.
 *
 * `message` is deliberately identical to what this module has always thrown —
 * the raw response body, truncated — because callers read it (see
 * `ApplyJob.onSubmit`, which `JSON.parse`s it into field errors). `status` and
 * `body` are additive: they let a caller branch on the status (e.g. 409 =
 * duplicate application) without changing any existing path.
 */
export class ApiError extends Error {
  readonly status: number;
  /** The response body parsed as JSON, or undefined when it wasn't JSON. */
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Duck-typed rather than `instanceof` on purpose: a bundle that loads this
 * module twice, or a test that mocks the module it travelled through, gets a
 * different class identity while the shape stays right.
 */
export const isApiError = (error: unknown): error is ApiError =>
  !!error &&
  typeof error === 'object' &&
  (error as ApiError).name === 'ApiError' &&
  typeof (error as ApiError).status === 'number';

const safeJsonParse = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const baseApiUrl = import.meta.env.VITE_BASE_API_URL;

export const fetchWrapper = async <Response = unknown, Params = unknown>(
  url: string,
  options: FetchOptions<Params> = {}
): Promise<FetchResponse<Response>> => {
  const urlParams = new URLSearchParams(window.location.search);
  // console.log('FetchWrapper called with URL:', url);
  const debug = !!urlParams.get('debug');
  const {
    method = 'GET',
    body,
    headers,
    params,
    timeout = 10000,
    ...rest
  } = options;

  const query = params
    ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
    : '';
  const fullUrl = baseApiUrl + `${url}${query}`;

  const controller = new AbortController();
  const id = setTimeout(() => {
    controller.abort();
  }, timeout);

  try {
    const response = await fetch(fullUrl, {
      method,
      // The apply flow rides on the backend's `qr_uid` cookie: it is set by
      // `record_qr_page_visit` and read back to hide positions the visitor
      // already applied to and to offer an unfinished interview to resume.
      // The cookie is httpOnly, so opting into credentials here is the only
      // way it ever reaches the API. Declared before `...rest` so a caller
      // can still override it.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      ...rest,
    });

    clearTimeout(id);

    if (!response.ok) {
      // if BE return just text instead of json
      const errorText: string | null = await response
        .text()
        .then((text: string) => text?.slice(0, 300))
        .catch(() => {
          console.error(
            'Error while parsing response text from fetchWrapper error'
          );
          return null;
        });

      if (errorText) {
        console.error('Response error: ', errorText);
        throw new ApiError(
          response.status,
          errorText,
          safeJsonParse(errorText)
        );
      }

      const error = await response.json().catch(() => {
        console.error(
          'Error while parsing response json from fetchWrapper error'
        );
        return null;
      });

      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        throw new ApiError(response.status, error.message, error);
      }

      if (response.status) {
        throw new ApiError(
          response.status,
          `Error ${response.status.toString()}`
        );
      }
    }

    if (response.status === 500) {
      throw new Error('Internal server error');
    }

    return (await response.json().catch(() => {
      console.error('Error while parsing response json from fetchWrapper body');
      return null;
    })) as Response;
  } catch (error: unknown) {
    console.error('Fetch error', error);
    Sentry.captureException(error);
    if (debug) {
      alert(error);
    }

    clearTimeout(id);
    if (
      error &&
      typeof error === 'object' &&
      'name' in error &&
      error.name === 'AbortError'
    ) {
      throw new Error('Request timeout');
    }
    throw error;
  }
};

export const get = async <T = unknown>(
  url: string,
  options?: FetchOptions
): Promise<FetchResponse<T>> => {
  return fetchWrapper(url, { ...options, method: 'GET' });
};

export const post = async <Response = unknown, Params = unknown>(
  url: string,
  body: Params,
  options?: FetchOptions<Params>
): Promise<FetchResponse<Response>> => {
  return fetchWrapper<Response, Params>(url, {
    body,
    ...options,
    method: 'POST',
  });
};

export const put = async <T = unknown>(
  url: string,
  options?: FetchOptions
): Promise<FetchResponse<T>> => {
  return fetchWrapper(url, { ...options, method: 'PUT' });
};

export const del = async <T = unknown>(
  url: string,
  options?: FetchOptions
): Promise<FetchResponse<T>> => {
  return fetchWrapper(url, { ...options, method: 'DELETE' });
};
